import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Fingerprint, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, priorityInfo } from "@/lib/format";
import { TAG_LABEL, TAG_CLS } from "@/components/workspace/tagStyles";

const TYPES = [["any", "Any type"], ["plate", "License plate"], ["phone", "Phone number"], ["dl", "Driver's license"], ["dob", "Date of birth"], ["name", "Name (manual tags)"], ["gps", "GPS coordinate"]];

export function EntitySearchCard({ onOpen }) {
  const [type, setType] = useState("any");
  const [value, setValue] = useState("");
  const [applied, setApplied] = useState(null);
  const q = useQuery({ queryKey: ["entity-search", applied], enabled: !!applied, queryFn: async () => (await api.get("/entities/search", { params: applied })).data, retry: false });
  return (
    <div className="space-y-2" data-testid="entity-search-card">
      <div className="flex items-start gap-2">
        <Fingerprint className="h-5 w-5 text-teal-500 mt-1" />
        <div><div className="overline-label">Intelligence index</div><div className="display text-lg font-semibold">Entity search</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Deliberate searches are recorded in the audit trail (who, what, when).</div></div>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); if (value.trim().length >= 2) setApplied({ type, value: value.trim() }); }} className="flex gap-1.5">
        <select data-testid="entity-search-type" value={type} onChange={(e) => setType(e.target.value)} className="field-input w-44">{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <input data-testid="entity-search-value" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Plate, phone, DL #, DOB (mm/dd/yyyy), name…" className="field-input flex-1 mono" autoFocus />
        <button data-testid="entity-search-submit" type="submit" className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold">Search</button>
      </form>
      {q.isError && <div className="text-xs text-red-500">{errMsg(q.error)}</div>}
      {applied && q.data && (
        <div className="space-y-1.5 max-h-[50vh] overflow-auto" data-testid="entity-search-results">
          <div className="mono text-xs text-muted-foreground">{q.data.length} related call{q.data.length === 1 ? "" : "s"} for {applied.type !== "any" ? `${TAG_LABEL[applied.type]} ` : ""}"{applied.value}"</div>
          {q.data.length === 0 && <div className="text-xs text-muted-foreground" data-testid="entity-search-empty">No prior contacts in the index.</div>}
          {q.data.map((g) => (
            <button key={g.incident.id} data-testid={`entity-result-${g.incident.incident_number}`} onClick={() => onOpen(g.incident.id)} className="w-full text-left rounded-md border px-2.5 py-1.5 row-hover">
              <div className="flex items-center gap-2 text-xs">
                <span className={`chip mono ${priorityInfo(g.incident.priority).cls}`}>{priorityInfo(g.incident.priority).short}</span>
                <span className="mono font-semibold">{g.incident.incident_number}</span>
                <span className="mono text-muted-foreground">{fmtDate(g.incident.received_at)}</span>
                <span className="font-medium">{g.incident.call_type_name}</span>
                <span className="text-muted-foreground truncate">{g.incident.location_raw}</span>
                <span className="ml-auto text-muted-foreground">{g.incident.disposition_label || g.incident.status.toUpperCase()}</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">{g.tags.map((t, i) => <span key={i} className={`chip mono ${TAG_CLS[t.type] || TAG_CLS.other}`}>{TAG_LABEL[t.type] || t.type} {t.display || t.value}{t.detection === "manual" ? " · manual" : ""}</span>)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
